/**
 * HL-12122 — WL funnel tracking that outlived the WL2 vs WL3 funnel test (WL3 won).
 * Sticky per browser (localStorage).
 *   entry_route      bridge | start_consultation | product_page | category | crm_email | direct
 *   entry_page       URL path of the page that led into the funnel
 *   customer_status  new | returning | active_subscriber
 * Plus the plan_type / medication_brand helpers the Care+ pages send with their events.
 */

import { MainTracker } from "../PixelTrackers/MainTracker";
import type { FunnelSplit } from "./funnelMentionRedirects";

export type PlanType = "monthly" | "one_time" | "prescription_only" | "care_plus";
export type EntryRoute = "bridge" | "start_consultation" | "product_page" | "category" | "crm_email" | "direct";

export type FunnelEntry = {
  entry_route: EntryRoute;
  entry_page: string;
};

const ENTRY_KEY = "wl_funnel_entry";

const readStorage = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

const writeStorage = (key: string, value: string): void => {
  try {
    localStorage.setItem(key, value);
  } catch {
    // storage blocked: the funnel still works, the entry is only not remembered
  }
};

const readJson = <T>(key: string): T | null => {
  const raw = readStorage(key);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
};

export type CustomerStatus = "new" | "returning" | "active_subscriber";

// Written by the CMS (wl-funnel-entry.ejs) on WL category / product pages: the funnel runs on another
// origin, so neither the CMS localStorage nor the referrer's path is visible here — a cookie is.
const ENTRY_COOKIE = "wl_funnel_entry";
const START_CONSULTATION_KEY = "wl_funnel_start_consultation_page";

const readEntryCookie = (): FunnelEntry | null => {
  try {
    const raw = document.cookie.split("; ").find((cookie) => cookie.startsWith(`${ENTRY_COOKIE}=`));
    if (!raw) return null;
    const entry = JSON.parse(decodeURIComponent(raw.slice(ENTRY_COOKIE.length + 1)));
    return entry?.entry_route && entry?.entry_page ? (entry as FunnelEntry) : null;
  } catch {
    return null;
  }
};

/**
 * Route into the funnel: a CRM email link (utm on the funnel URL) > the CMS page that sent the visitor
 * (category / product page, from the cookie) > the start-consultation page > direct.
 */
export const getEntryRoute = (
  search: string,
  cmsEntry: FunnelEntry | null,
  startConsultationPage: string | null,
  pathname: string,
): FunnelEntry => {
  const params = new URLSearchParams(search);
  if (params.get("utm_medium")?.toLowerCase() === "email" || params.get("utm_source")?.toLowerCase() === "email") {
    return { entry_route: "crm_email", entry_page: pathname };
  }
  if (cmsEntry) return cmsEntry;
  if (startConsultationPage) return { entry_route: "start_consultation", entry_page: startConsultationPage };
  return { entry_route: "direct", entry_page: pathname };
};

/** The start-consultation page remembers itself, so the questionnaire can report that route. */
export const markStartConsultationEntry = (): void => writeStorage(START_CONSULTATION_KEY, window.location.pathname);

/** Entry route + page, fixed by the first WL questionnaire load of this browser. */
export const captureFunnelEntry = (): FunnelEntry => {
  const stored = readJson<FunnelEntry>(ENTRY_KEY);
  if (stored?.entry_route) return stored;
  const entry = getEntryRoute(window.location.search, readEntryCookie(), readStorage(START_CONSULTATION_KEY), window.location.pathname);
  writeStorage(ENTRY_KEY, JSON.stringify(entry));
  return entry;
};

/** customer_status on every later event (new | returning | active_subscriber). */
export const registerCustomerStatus = (status: CustomerStatus | undefined): void => {
  if (status) MainTracker.superProperties({ customer_status: status });
};

/** entry_route / entry_page on every later event. */
export const registerFunnelEntry = (): void => {
  MainTracker.superProperties({ ...captureFunnelEntry() });
};

/**
 * HL-12704: UK WL vs WL2 arm as `funnel_variant` (wl | wl2) on every later event; Paid Order gets it from
 * the Mixpanel cookie the order stores. `split` is null outside GB weight_loss, so nothing else is tagged.
 * Weight 0 tags wl, so a rollback relabels earlier WL2 browsers.
 */
export const registerSplitFunnelVariant = (split: FunnelSplit | null | undefined, splitUrl: string | null): void => {
  if (split?.mention) MainTracker.superProperties({ funnel_variant: splitUrl ? "wl2" : "wl" });
};

/** Same rule as the server (CarePlusManager.getPlanType): plan tier "monthly" is the V3 Monthly plan, other tiers are Care+. */
export const getPlanType = (oneTime: boolean, prescriptionOnly: boolean, tier?: string | null): PlanType => {
  if (prescriptionOnly) return "prescription_only";
  if (oneTime) return "one_time";
  return tier === "monthly" ? "monthly" : "care_plus";
};

/**
 * `medication_brand` from the catalog title (the Choose Treatment payload has no catalog mention).
 * Same values the server derives from the catalog mention (CarePlusManager.getMedicationBrand).
 */
export const getMedicationBrandFromTitle = (title: string | null | undefined): string | null => {
  if (!title) return null;
  const normalized = title.toLowerCase();
  if (normalized.includes("wegovy")) return /tablet|pill/.test(normalized) ? "wegovy_pill" : "wegovy_pen";
  if (normalized.includes("mounjaro")) return /durchstech|vial/.test(normalized) ? "mounjaro_vials" : "mounjaro";
  const firstWord = normalized.replace(/[^a-z0-9\s-]/g, "").trim().split(/[\s-]+/)[0];
  return firstWord || null;
};
